/**
 * Voice AI — Agent Detail Panel
 * Header, tab bar, and tab routing for a single agent.
 * Delegates all tab content to sub-components.
 * Dispatches: back, agent-updated, agent-deleted, toast
 */

import { LitElement, html } from 'lit';
import { designSystem, componentStyles } from '../../styles/design-system.js';
import { agentStyles } from '../../styles/agent-styles.js';
import { iconStyles } from '../../styles/icon-styles.js';
import { agentService } from '../../services/agent-service.js';
import { confirmService } from '../../services/confirm-service.js';

import './agent-overview-tab.js';
import './agent-config-tab.js';
import './agent-voice-tab.js';
import './agent-pipeline-tab.js';
import './agent-realism-tab.js';
import './agent-knowledge-tab.js';
import './agent-testing-tab.js';
import './agent-activity-tab.js';
import './agent-guardrails-tab.js';
import './agent-memory-tab.js';
import './agent-ai-setup-tab.js';

export class AgentDetail extends LitElement {
  static properties = {
    agent: { type: Object },
    saving: { type: Boolean },
    llmProviders: { type: Array },
    sttProviders: { type: Array },
    ttsProviders: { type: Array },
    contextualLlmProviders: { type: Array },
    _activeTab: { type: String, state: true },
  };

  static styles = [designSystem, componentStyles, agentStyles];

  constructor() {
    super();
    this.agent = null;
    this.saving = false;
    this.llmProviders = [];
    this.sttProviders = [];
    this.ttsProviders = [];
    this.contextualLlmProviders = [];
    this._activeTab = 'overview';
  }

  _setTab(tab) {
    this._activeTab = tab;
  }

  _back() {
    this.dispatchEvent(new CustomEvent('back'));
  }

  _toast(message) {
    this.dispatchEvent(new CustomEvent('toast', { detail: { message } }));
  }

  async _saveAgent(updatedAgent) {
    if (this.saving) return;
    this.saving = true;
    try {
      const body = agentService.toSnakeCase(updatedAgent);
      const data = await agentService.updateAgent(updatedAgent.id, body);
      if (data.success) {
        const transformed = agentService.transformAgent(data.agent);
        transformed.lastActive = agentService.relativeTime(transformed.lastActiveAt);
        this.agent = transformed;
        this.dispatchEvent(new CustomEvent('agent-updated', { detail: { agent: transformed } }));
        this._toast('Agent configuration saved');
      } else {
        this._toast('Save failed: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('[AgentDetail] Save failed:', err);
      this._toast('Save failed: ' + err.message);
    } finally {
      this.saving = false;
    }
  }

  async _deleteAgent() {
    const agent = this.agent;
    if (!agent) return;
    const confirmed = await confirmService.show({
      title: 'Delete Agent',
      message: `Delete "${agent.name}"?`,
      description: 'This cannot be undone.',
    });
    if (!confirmed) return;
    try {
      const data = await agentService.deleteAgent(agent.id);
      if (data.success) {
        this.dispatchEvent(new CustomEvent('agent-deleted', { detail: { id: agent.id } }));
        this._toast('Agent deleted');
      } else {
        this._toast('Delete failed: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      this._toast('Delete failed: ' + err.message);
    }
  }

  _handleTabSave(e) {
    this._saveAgent(e.detail.agent);
  }

  _handleAgentChange(e) {
    this.agent = e.detail.agent;
  }

  render() {
    if (!this.agent) return html``;
    const agent = this.agent;
    const baseTabs = ['overview', 'configuration', 'ai_setup', 'voice', 'pipeline', 'realism', 'knowledge', 'testing', 'activity'];
    const TABS = agent.isAgentOne ? [...baseTabs, 'memory', 'guardrails'] : baseTabs;
    const TAB_LABELS = {
      overview: 'Overview',
      configuration: 'Configuration',
      ai_setup: 'AI Setup',
      voice: 'Voice',
      pipeline: 'Pipeline',
      realism: 'Realism',
      knowledge: 'Knowledgebase',
      testing: 'Testing',
      activity: 'Activity',
      memory: html`<i class="fa-sharp fa-light fa-brain-circuit" style="color:#9B7BD4;margin-right:2px;"></i> Memory`,
      guardrails: '\u2605 Agent One',
    };

    return html`
      ${iconStyles}
      <div class="detail-panel">
        <button class="detail-back-btn" @click=${this._back}>
          <i class="fa-sharp fa-light fa-arrow-left"></i>
          Back to Agents
        </button>

        <div class="detail-header">
          <div class="detail-avatar color-${agent.color}">
            <i class="fa-sharp-duotone fa-solid fa-robot"></i>
          </div>
          <div class="detail-title-area">
            <div class="detail-title">${agent.name}</div>
            <div class="detail-subtitle">
              <span class="badge ${agent.status === 'active' ? 'badge-success' : 'badge-neutral'}" style="margin-right: var(--space-2);">
                <span class="status-dot ${agent.status === 'active' ? 'active' : 'inactive'}"></span>
                ${agent.status === 'active' ? 'Active' : 'Inactive'}
              </span>
              ${agent.description}
            </div>
          </div>
        </div>

        <div class="detail-tabs">
          ${TABS.map(tab => html`
            <button
              class="detail-tab ${this._activeTab === tab ? 'active' : ''}"
              @click=${() => this._setTab(tab)}
            >${TAB_LABELS[tab]}</button>
          `)}
        </div>

        ${this._activeTab === 'overview' ? html`
          <agent-overview-tab .agent=${agent} @switch-tab=${(e) => this._setTab(e.detail)}></agent-overview-tab>
        ` : ''}
        ${this._activeTab === 'configuration' ? html`
          <agent-config-tab
            .agent=${agent}
            .saving=${this.saving}
            @save=${this._handleTabSave}
            @delete=${() => this._deleteAgent()}
            @back=${this._back}
            @agent-changed=${this._handleAgentChange}
          ></agent-config-tab>
        ` : ''}
        ${this._activeTab === 'ai_setup' ? html`
          <agent-ai-setup-tab
            .agent=${agent}
            .saving=${this.saving}
            @save=${this._handleTabSave}
            @agent-changed=${this._handleAgentChange}
          ></agent-ai-setup-tab>
        ` : ''}
        ${this._activeTab === 'voice' ? html`
          <agent-voice-tab
            .agent=${agent}
            .saving=${this.saving}
            .ttsProviders=${this.ttsProviders}
            @save=${this._handleTabSave}
            @agent-changed=${this._handleAgentChange}
          ></agent-voice-tab>
        ` : ''}
        ${this._activeTab === 'pipeline' ? html`
          <agent-pipeline-tab
            .agent=${agent}
            .saving=${this.saving}
            .llmProviders=${this.llmProviders}
            .sttProviders=${this.sttProviders}
            .ttsProviders=${this.ttsProviders}
            .contextualLlmProviders=${this.contextualLlmProviders}
            @save=${this._handleTabSave}
            @agent-changed=${this._handleAgentChange}
          ></agent-pipeline-tab>
        ` : ''}
        ${this._activeTab === 'realism' ? html`
          <agent-realism-tab
            .agent=${agent}
            .saving=${this.saving}
            @save=${this._handleTabSave}
            @agent-changed=${this._handleAgentChange}
          ></agent-realism-tab>
        ` : ''}
        ${this._activeTab === 'knowledge' ? html`
          <agent-knowledge-tab .agent=${agent}></agent-knowledge-tab>
        ` : ''}
        ${this._activeTab === 'testing' ? html`
          <agent-testing-tab .agent=${agent}></agent-testing-tab>
        ` : ''}
        ${this._activeTab === 'activity' ? html`
          <agent-activity-tab .agent=${agent}></agent-activity-tab>
        ` : ''}
        ${this._activeTab === 'memory' && agent.isAgentOne ? html`
          <agent-memory-tab .agent=${agent}></agent-memory-tab>
        ` : ''}
        ${this._activeTab === 'guardrails' && agent.isAgentOne ? html`
          <agent-guardrails-tab
            .agent=${agent}
            .saving=${this.saving}
            @save=${this._handleTabSave}
            @agent-changed=${this._handleAgentChange}
          ></agent-guardrails-tab>
        ` : ''}
      </div>
    `;
  }
}

customElements.define('agent-detail', AgentDetail);
